<template>
  <div class="user-access">
    <slot name="label" />

    <div class="user-access__content">
      <div class="user-access__left">
        <div class="user-access__item">
          <InputComponent :label="$t('userLevel')" class="mb-4">
            <template #after-label>
              <HelperPopover template="user-level-helper" />
            </template>
            <Radio v-model="valueComp" :items="items.global" />
          </InputComponent>
          <InputComponent v-if="!['admin', 'guest'].some(r => valueComp.includes(r))" :label="$tc('access', 2)">
            <template #after-label>
              <HelperPopover template="access-helper" />
            </template>

            <ChecklistComponent :value="value" @input="$ev => $emit('input', $ev)" :items="items.local" />
          </InputComponent>
        </div>
        <slot name="left" />
      </div>
      <div class="user-access__right">
        <slot name="right" />
        <InputComponent v-if="!valueComp.includes('guest')" :label="$t('devtools')" class="user-access__item">
          <template #after-label>
            <HelperPopover template="devtools-helper" />
          </template>
          <ChecklistComponent :value="value" @input="$ev => $emit('input', $ev)" :items="items.developer" />
        </InputComponent>
      </div>
    </div>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import ChecklistComponent from '@/components/Select/Checklist'
import Radio from '@kvass/radio'
import HelperPopover from '@/components/HelperPopover/Component'

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    valueComp: {
      get() {
        if (['guest', 'admin'].every(r => !this.value.includes(r))) return 'default'
        return ['guest', 'admin'].find(r => this.value.includes(r))
      },
      set(newValue) {
        let items = this.value

        const oldValue = ['guest', 'admin'].find(r => items.includes(r))
        const index = items.indexOf(oldValue)
        if (index >= 0) items.splice(index, 1)

        let compatibleRoles = {
          admin: ['developer'],
          default: ['developer'],
          guest: [],
        }

        let map = compatibleRoles[newValue]
        if (map) items = items.filter(v => map.includes(v))

        this.$emit(
          'input',
          [newValue, ...items].filter(i => i !== 'default'),
        )
      },
    },

    items() {
      return {
        global: [
          {
            value: 'admin',
            category: { value: 'global' },
            label: this.$t('administrator'),
            disabled: this.value.includes('owner'),
          },
          {
            value: 'default',
            category: { value: 'global' },
            label: this.$t('userIsDefault'),
            disabled: this.value.includes('owner'),
          },
          {
            value: 'guest',
            category: { value: 'global' },
            label: this.$t('userIsGuest'),
            disabled: this.value.includes('owner'),
          },
        ],

        local: [
          {
            value: 'contacts',
            label: this.$tc('contact', 2),
            disabled: ['guest', 'admin'].some(r => this.value.includes(r)),
          },
          {
            value: 'residentialTemplates',
            label: this.$tc('unitCatalog', 1),
            disabled: ['guest', 'admin'].some(r => this.value.includes(r)),
          },
          {
            value: 'products',
            label: this.$tc('product', 2),
            disabled: ['guest', 'admin'].some(r => this.value.includes(r)),
          },
        ],
        developer: [
          {
            value: 'developer',
            label: this.$t('userIsDeveloper'),
            disabled: this.value.includes('guest'),
          },
        ],
      }
    },
  },
  components: {
    ChecklistComponent,
    InputComponent,
    Radio,
    HelperPopover,
  },
}
</script>

<style lang="scss">
.user-access {
  &__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  &__left,
  &__right {
    display: flex;
    gap: 1rem;
    flex-direction: column;

    .kvass-radio {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  &__item {
    border: 1px solid var(--vue-elder-border-color);
    border-radius: var(--vue-elder-border-radius, 3px);
    padding: 1rem;

    .elder-input__field {
      border: none;
    }
  }
}
</style>
